import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateWallet } from "@/features/wallet/api/use-create-wallet";
import type { WalletDto } from "@/features/wallet/api/dto";
import {
  createWalletSchema,
  type WalletFormValues,
} from "@/features/wallet/schemas/wallet.schema";
import {
  WALLET_KYC_LEVELS,
  WALLET_OWNER_TYPES,
  WALLET_STATUSES,
} from "@/features/shared/api/enums";
import { usePartySelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: WalletFormValues = {
  ownerType: "INDIVIDUAL",
  ownerPartyId: "",
  keycloakUserId: "",
  currency: "GNF",
  kycLevel: "TIER0",
  status: "PENDING_KYC",
};

/**
 * Formulaire de création d'un portefeuille (`POST /Wallets/add`) — étape 3
 * du parcours d'inscription : il naît en PENDING_KYC / TIER0. TanStack Form
 * + schéma zod mémoïsé sur la langue courante (règle projet : jamais
 * d'instance figée). Preview MutationConfirmPreview avant l'appel API.
 */
export function WalletFormDialog({
  open,
  onOpenChange,
  onCreated,
  initialOwnerType,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Appelé après une création réussie (enchaînement de parcours). */
  onCreated?: (wallet: WalletDto) => void;
  /** Pré-sélection du type (ex. PLATFORM pour l'approvisionnement §5.5). */
  initialOwnerType?: WalletFormValues["ownerType"];
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Les clés wallet.wallets.* vivent dans features/wallet/i18n/wallets.json
  // en attendant leur fusion dans src/locales par le coordinateur.
  const tWallet = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createWalletSchema(), [i18n.language]);

  const createWallet = useCreateWallet();
  const pending = createWallet.isPending;
  const [preview, setPreview] = useState<WalletDto | null>(null);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs par défaut du parcours d'inscription.
  const form = useForm({
    defaultValues: {
      ...EMPTY_VALUES,
      ...(initialOwnerType ? { ownerType: initialOwnerType } : {}),
    },
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: WalletDto = {
        ownerType: parsed.data.ownerType,
        ownerPartyId: parsed.data.ownerPartyId,
        keycloakUserId: parsed.data.keycloakUserId || undefined,
        currency: parsed.data.currency,
        kycLevel: parsed.data.kycLevel,
        status: parsed.data.status,
      };
      setPreview(payload);
    },
  });

  const fieldError =
    (name: keyof WalletFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tWallet("wallet.wallets.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="ownerType" validators={{ onChange: fieldError("ownerType") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tWallet("wallet.wallets.form.ownerType")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as WalletFormValues["ownerType"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WALLET_OWNER_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="kycLevel" validators={{ onChange: fieldError("kycLevel") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tWallet("wallet.wallets.form.kycLevel")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as WalletFormValues["kycLevel"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WALLET_KYC_LEVELS.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field
            name="ownerPartyId"
            validators={{ onChange: fieldError("ownerPartyId") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="ownerPartyId">
                  {tWallet("wallet.wallets.form.ownerPartyId")}
                </Label>
                <EntitySearchableSelect
                  id="ownerPartyId"
                  options={partyOptions}
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tWallet(
                    "wallet.wallets.form.ownerPartyIdPlaceholder"
                  )}
                  isLoading={partiesLoading}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="keycloakUserId"
            validators={{ onChange: fieldError("keycloakUserId") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="keycloakUserId">
                  {tWallet("wallet.wallets.form.keycloakUserId")}
                </Label>
                <Input
                  id="keycloakUserId"
                  value={field.state.value}
                  placeholder={tWallet(
                    "wallet.wallets.form.keycloakUserIdPlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="currency" validators={{ onChange: fieldError("currency") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="currency">
                  {tWallet("wallet.wallets.form.currency")}
                </Label>
                <Input
                  id="currency"
                  value={field.state.value}
                  placeholder={tWallet("wallet.wallets.form.currencyPlaceholder")}
                  maxLength={3}
                  className="uppercase"
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="status" validators={{ onChange: fieldError("status") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tWallet("wallet.wallets.form.status")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as WalletFormValues["status"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WALLET_STATUSES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tWallet}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        createWallet.mutate(preview, {
          onSuccess: (data) => {
            notifSilence(tWallet("wallet.wallets.feedback.created"));
            if (data && typeof data === "object" && "id" in data) {
              onCreated?.(data as unknown as WalletDto);
            }
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
